<template>
  <ArticleBackComponent />
  <section
    class="article-title-with-image-module-component"
    :class="{ 'no-media-after': noMediaAfter }">
    <div
      :class="{
        'sa-container': !showPostImage,
        'rr-container': showPostImage,
      }">
      <div
        v-if="showPostImage && postImageUrl"
        class="image-container">
        <img
          class="article-image"
          :src="postImageUrl"
          :alt="postImageAlt" />
      </div>

      <div class="article-heading">
        <p
          v-if="categoryName"
          class="article-category">
          <span v-html="categoryName"></span>
        </p>

        <h2
          v-if="usePostTitle && postTitle"
          class="article-title"
          v-html="postTitle"></h2>

        <div
          v-else-if="data.new_title"
          class="article-title custom-title"
          v-html="data.new_title"></div>

        <div
          v-if="data.subtitle"
          class="article-subtitle"
          v-html="data.subtitle"></div>

        <div
          class="divider-container"
          :class="{ 'no-meta': !(author || publishedDate) }">
          <BicolorLine
            :bigger="false"
            left="approachable"
            right="progressive" />
        </div>

        <div
          v-if="author || publishedDate"
          class="article-meta">
          <p
            v-if="author"
            class="article-author">
            By <span v-html="author"></span>
          </p>
          <p
            v-if="publishedDate"
            class="article-date">
            {{ publishedDate }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import BicolorLine from '@/components/utility/BicolorLine.vue'
import { computed } from 'vue'
import ArticleBackComponent from './ArticleBackComponent.vue'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  post: {
    type: Object,
    required: true,
  },
  noMediaAfter: {
    type: Boolean,
    default: false,
  },
})

const enabled = (value) => value === true || value === 1 || value === '1'

const articleData = computed(() => props.post?.acf || {})
const usePostTitle = computed(() => enabled(props.data?.use_post_title))
const showPostImage = computed(() => enabled(props.data?.enable_post_image))
const postTitle = computed(() => props.post?.title?.rendered || '')
const author = computed(() => articleData.value?.author || '')
const publishedDate = computed(() => articleData.value?.published_date || '')
const postImageUrl = computed(
  () => articleData.value?.single_article_image?.url || ''
)
const postImageAlt = computed(
  () => articleData.value?.single_article_image?.alt || ''
)
const categoryName = computed(() => {
  const category = articleData.value?.article_categories?.[0]

  if (category && typeof category === 'object') {
    return category.name || ''
  }

  return typeof category === 'string' && !/^\d+$/.test(category) ? category : ''
})
</script>

<style lang="scss" scoped>
.article-title-with-image-module-component {
  @apply relative bg-white lg:pt-10 lg:pb-10;

  &.no-media-after {
    @apply lg:pb-4;
  }

  .sa-container,
  .rr-container {
    @apply flex flex-col lg:flex-row gap-8 items-start;

    &.with-image {
      @apply max-w-[1300px];

      .image-container {
        @apply order-2 lg:order-1;
      }

      .article-heading {
        @apply order-1 lg:order-2;
      }
    }

    .article-heading {
      .divider-container {
        @apply mt-[36px] mb-[36px] lg:mt-[38px] lg:mb-[38px];

        &.no-meta {
          @apply mb-0 lg:mb-0;
        }
      }

      .article-category {
        @apply mb-5 border border-balanced relative w-fit rounded-[27px] px-4 py-2;
        @apply lg:mb-8;

        span {
          @apply relative text-[14px] leading-[1.2] text-balanced lg:text-[22px];

          // &:before {
          //   @apply absolute bottom-[3px] left-0 w-full h-[1px] bg-balanced opacity-100;
          //   content: '';
          // }
        }
      }

      .article-title {
        @apply flex flex-col gap-6 header-2 text-humble lg:gap-[15px];

        &:deep(h1),
        &:deep(h2),
        &:deep(h3),
        &:deep(h4),
        &:deep(h5),
        &:deep(h6),
        &:deep(p) {
          @apply m-0 text-inherit;
        }

        &:deep(p) {
          @apply header-4 font-normal;
        }
      }

      .article-subtitle {
        @apply mt-6 lg:mt-[15px];

        &:deep(p) {
          @apply header-4 text-[22px] lg:text-[26px] font-normal text-balanced;
        }
      }

      .article-meta {
        @apply mt-6 flex flex-col gap-2;

        .article-author {
          @apply header-5 normal-case;
        }
        .article-date {
          @apply header-6 normal-case;
        }
      }
    }

    .image-container {
      @apply lg:flex-[1_0_auto];

      .article-image {
        @apply block h-auto w-full rounded-[12px] object-cover lg:mt-10 lg:rounded-[27px];
        @apply lg:h-[428px] lg:mt-0 lg:w-[428px] xl:h-[528px] xl:w-[528px];
      }
    }
  }
}
</style>
